/*-----------------------------------------------------------------------------/
/ Custom Theme JS
/-----------------------------------------------------------------------------*/

(() => {
  const BUNDLE_SELECTOR = '[data-buy-three-section]';
  const CHECK_ICON_TEMPLATE = '<i class="icon icon--tick" aria-hidden="true"></i>';
  const DISCOUNT_ENDPOINT = (code) =>
    `${window.Shopify.routes.root}discount/${encodeURIComponent(code)}`;

  const dedupeCartItems = (cart) => {
    if (!cart || !Array.isArray(cart.items)) return cart;
    return {
      ...cart,
      items: cart.items.map((item) => {
        if (
          item.line_level_discount_allocations &&
          item.line_level_discount_allocations.length > 0
        ) {
          const discountSuffix = item.line_level_discount_allocations.reduce(
            (acc, discount) => `${acc}_discount-${discount.amount}`,
            '',
          );
          return {
            ...item,
            key: `${item.key}${discountSuffix}`,
          };
        }
        return item;
      }),
    };
  };

  class BuyThreeGetOneController {
    constructor(root) {
      this.root = root;
      this.requiredProductIds = this._parseIds(root.dataset.bundleRequired);
      this.freeProductId = Number(root.dataset.bundleFreeProduct || 0);
      this.freeVariantId = Number(root.dataset.bundleFreeVariant || 0);
      this.discountCode = root.dataset.bundleDiscountCode || '';
      this.cartStore = window.theme?.cart?.store || null;
      this.triggers = Array.from(
        root.querySelectorAll('.quick-shop__trigger.js-quickshop-trigger'),
      );
      this._addingGift = false;
      this._applyingDiscount = false;
      this._onStoreChange = this._onStoreChange.bind(this);
      this._disableLinkHandlers = new WeakMap();

      this._initTriggerDefaults();

      if (
        !this.cartStore ||
        !this.requiredProductIds.length ||
        !this.freeVariantId
      ) {
        return;
      }

      this.unsubscribe = this.cartStore.subscribe(this._onStoreChange);
      this._onStoreChange(this.cartStore.getState());
    }

    destroy() {
      if (this.unsubscribe) {
        this.unsubscribe();
        this.unsubscribe = null;
      }
    }

    _parseIds(idsString = '') {
      if (!idsString) return [];
      return idsString
        .split(',')
        .map((id) => Number(id.trim()))
        .filter((id) => !Number.isNaN(id) && id > 0);
    }

    _initTriggerDefaults() {
      this.triggers.forEach((trigger) => {
        if (!trigger.dataset.bundleDefaultIcon) {
          trigger.dataset.bundleDefaultIcon = trigger.innerHTML;
        }
      });
    }

    _onStoreChange(state) {
      const cart = state?.products || this.cartStore.getState().products;
      if (!cart || !Array.isArray(cart.items)) return;

      const itemsMap = cart.items.reduce((acc, item) => {
        const productId = item.product_id;
        if (!productId) return acc;
        acc[productId] = (acc[productId] || 0) + item.quantity;
        return acc;
      }, {});

      this._updateTriggerStates(itemsMap);

      const hasAllRequired = this.requiredProductIds.every(
        (id) => itemsMap[id],
      );
      const hasFreeProduct = Boolean(itemsMap[this.freeProductId]);

      if (hasAllRequired && !hasFreeProduct) {
        this._addGiftAndDiscount();
        return;
      }

      if (hasAllRequired && hasFreeProduct) {
        this._applyDiscount(cart);
      }
    }

    _updateTriggerStates(itemsMap) {
      const hasAllRequired = this.requiredProductIds.every(
        (id) => itemsMap[id],
      );
      const hasFreeProduct = Boolean(itemsMap[this.freeProductId]);

      this.triggers.forEach((trigger) => {
        const productCard = trigger.closest('product-card');
        if (!productCard) return;
        const productId = Number(productCard.dataset.productId || 0);
        if (!productId) return;

        const isFreeCard = productCard.dataset.freeProduct === 'true';
        const isComplete = isFreeCard
          ? hasFreeProduct
          : Boolean(itemsMap[productId]);

        const shouldDisable = isFreeCard
          ? !hasAllRequired || hasFreeProduct || this._addingGift
          : isComplete;

        this._setTriggerState(trigger, { disabled: shouldDisable, complete: isComplete });
      });
    }

    _setTriggerState(trigger, { disabled, complete }) {
      const nextState = `${disabled ? 'disabled' : 'enabled'}-${
        complete ? 'complete' : 'idle'
      }`;
      if (trigger.dataset.bundleState === nextState) return;

      trigger.dataset.bundleState = nextState;

      if ('disabled' in trigger) {
        trigger.disabled = disabled;
      } else {
        this._toggleLinkDisable(trigger, disabled);
      }

      trigger.classList.toggle('is-disabled', disabled);
      if (disabled) {
        trigger.setAttribute('aria-disabled', 'true');
      } else {
        trigger.removeAttribute('aria-disabled');
      }

      this._swapTriggerIcon(trigger, complete);
    }

    _toggleLinkDisable(trigger, disabled) {
      if (!disabled) {
        const handler = this._disableLinkHandlers.get(trigger);
        if (handler) {
          trigger.removeEventListener('click', handler);
          this._disableLinkHandlers.delete(trigger);
        }
        trigger.removeAttribute('tabindex');
        return;
      }

      if (this._disableLinkHandlers.has(trigger)) return;
      const handler = (event) => {
        event.preventDefault();
        event.stopPropagation();
      };
      trigger.addEventListener('click', handler, true);
      trigger.setAttribute('tabindex', '-1');
      this._disableLinkHandlers.set(trigger, handler);
    }

    _swapTriggerIcon(trigger, complete) {
      const defaultIcon = trigger.dataset.bundleDefaultIcon;
      if (!defaultIcon) return;

      if (complete) {
        trigger.innerHTML = CHECK_ICON_TEMPLATE;
        return;
      }

      trigger.innerHTML = defaultIcon;
    }

    async _addGiftAndDiscount() {
      if (this._addingGift || !this.freeVariantId) return;
      this._addingGift = true;

      try {
        const properties =
          this.discountCode !== ''
            ? { 'Discount Code': this.discountCode }
            : undefined;

        await this.cartStore.getState().add({
          id: this.freeVariantId,
          quantity: 1,
          ...(properties ? { properties } : {}),
        });

        await this._applyDiscount(this.cartStore.getState().products);
      } catch (error) {
        console.error('Buy Three bundle: unable to add gift', error);
      } finally {
        this._addingGift = false;
      }
    }

    async _applyDiscount(cartSnapshot) {
      if (!this.discountCode || this._applyingDiscount) return;
      if (this._isDiscountAlreadyApplied(cartSnapshot)) return;

      this._applyingDiscount = true;
      try {
        await fetch(DISCOUNT_ENDPOINT(this.discountCode), {
          credentials: 'same-origin',
        });
        await this._refreshCart();
      } catch (error) {
        console.error('Buy Three bundle: unable to apply discount', error);
      } finally {
        this._applyingDiscount = false;
      }
    }

    _isDiscountAlreadyApplied(cart) {
      if (!cart) return false;
      const applications = cart.cart_level_discount_applications || [];
      if (!applications.length) return false;

      return applications.some((application) => {
        const title =
          application.title ||
          application.discount_application?.title ||
          '';
        return (
          title &&
          title.toLowerCase() === this.discountCode.toLowerCase()
        );
      });
    }

    async _refreshCart() {
      try {
        const response = await fetch(`${window.Shopify.routes.root}cart.js`, {
          credentials: 'same-origin',
          cache: 'no-store',
        });
        const cart = await response.json();
        const normalized = dedupeCartItems(cart);
        this.cartStore.setState((state) => ({
          ...state,
          products: normalized,
        }));
        return normalized;
      } catch (error) {
        console.error('Buy Three bundle: unable to refresh cart', error);
        return null;
      }
    }
  }

  const initBundles = () => {
    const roots = document.querySelectorAll(BUNDLE_SELECTOR);
    if (!roots.length) return;

    const ensureStore = () => {
      if (!(window.theme && window.theme.cart && window.theme.cart.store)) {
        window.requestAnimationFrame(ensureStore);
        return;
      }

      roots.forEach((root) => {
        if (root.__buyThreeController) return;
        root.__buyThreeController = new BuyThreeGetOneController(root);
      });
    };

    ensureStore();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initBundles);
  } else {
    initBundles();
  }
})();